import { memo, useCallback } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';

import {
  Box,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import type { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';

import { useUserVouchers } from './hooks/useUserVouchers';
import type {
  UserVoucherItem,
  UserVoucherSummary,
  TranslateFn,
} from './types';
import { formatInt, getErrorMessage, shortenWallet } from './utils';

const I18N_PREFIX = 'module.worldCup.userVouchers';

const tUV = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionData>();
  const canWrite = hasPermission(permissions?.permissions, 'world-cup-user-vouchers', 'write');

  const {
    summary,
    items,
    totalCount,
    page,
    pageCount,
    search,
    loading,
    updating,
    refetch,
    setPage,
    setSearch,
    ban,
  } = useUserVouchers();

  const handleBanChange = useCallback(
    async (item: UserVoucherItem, nextBanned: boolean) => {
      let reason = '';
      if (nextBanned) {
        const input = window.prompt(tUV(t, 'confirm.banReason'));
        if (input === null) return;
        reason = input.trim();
      } else if (!window.confirm(tUV(t, 'confirm.unban'))) {
        return;
      }

      try {
        await ban({ userId: item.userId, banned: nextBanned, reason: reason || undefined });
        await refetch();
        notify(
          tUV(t, nextBanned ? 'notify.banSuccess' : 'notify.unbanSuccess'),
          { type: 'success' }
        );
      } catch (err) {
        notify(getErrorMessage(err) || tUV(t, 'notify.banFailed'), { type: 'error' });
      }
    },
    [ban, notify, refetch, t]
  );

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tUV(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupUserVouchers') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tUV(t, 'description')}
      </Typography>

      <Stack spacing={3}>
        <SummaryRow summary={summary} t={t} />

        <Card sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 2 }}
            spacing={2}
          >
            <Box>
              <Typography variant="h6">{tUV(t, 'list.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tUV(t, 'list.desc', { count: totalCount })}
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder={tUV(t, 'list.searchPlaceholder')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>{tUV(t, 'list.col.rank')}</TableCell>
                  <TableCell>{tUV(t, 'list.col.userId')}</TableCell>
                  <TableCell>{tUV(t, 'list.col.wallet')}</TableCell>
                  <TableCell align="right">{tUV(t, 'list.col.currentVouchers')}</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    {tUV(t, 'list.col.banned')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !items.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : items.length ? (
                  items.map((item) => (
                    <TableRow
                      key={item.userId}
                      hover
                      sx={
                        item.banned
                          ? {
                              bgcolor: 'error.lighter',
                              opacity: 0.7,
                              '& td': { color: 'text.disabled' },
                              '&:hover': { bgcolor: 'error.lighter' },
                            }
                          : undefined
                      }
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.rank}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                        >
                          {item.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            color="text.secondary"
                          >
                            {shortenWallet(item.address)}
                          </Typography>
                          {item.banned ? (
                            <Chip
                              label={tUV(t, 'list.bannedTag')}
                              color="error"
                              size="small"
                              icon={<Iconify icon="eva:slash-fill" />}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {formatInt(item.currentVouchers)} {tUV(t, 'list.voucherUnit')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Switch
                          checked={item.banned}
                          disabled={updating || !canWrite}
                          color="error"
                          onChange={(_, checked) => handleBanChange(item, checked)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Stack alignItems="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {tUV(t, 'list.empty')}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pageCount > 1 ? (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                disabled={loading}
                shape="rounded"
              />
            </Stack>
          ) : null}
        </Card>
      </Stack>
    </DashboardContent>
  );
};

const SummaryRow = memo(
  ({ summary, t }: { summary: UserVoucherSummary; t: TranslateFn }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 2,
      }}
    >
      <StatCard
        label={tUV(t, 'summary.holderCount')}
        value={formatInt(summary.holderCount)}
        sub={tUV(t, 'summary.holderUnit')}
        accent="secondary.main"
      />
      <StatCard
        label={tUV(t, 'summary.circulatingVouchers')}
        value={formatInt(summary.circulatingVouchers)}
        sub={tUV(t, 'summary.circulatingDesc')}
        accent="success.main"
      />
      <StatCard
        label={tUV(t, 'summary.bannedCount')}
        value={formatInt(summary.bannedCount)}
        sub={tUV(t, 'summary.bannedDesc')}
        accent="warning.main"
      />
    </Box>
  )
);

SummaryRow.displayName = 'SummaryRow';

const StatCard = memo(
  ({ label, value, sub, accent }: { label: string; value: string; sub: string; accent: string }) => (
    <Card sx={{ p: 3, borderLeft: 4, borderColor: accent }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
        {sub}
      </Typography>
    </Card>
  )
);

StatCard.displayName = 'StatCard';
